/** @jsx jsx*/
import { jsx } from 'theme-ui';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Box, Flex } from 'theme-ui';
import Link from '../components/Link';
import CustomsClearanceStatus from './CustomsClearanceStatus';
import { dateToTimezoneDate } from '../utils/date';
import AlertNotification from './AlertNotification';

export const TrackingEvents = ({ events }) => {
  const { t: translate } = useTranslation();
  if (!events) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'relative',
        mt: 4,
        '::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: 11,
          width: 2,
          bg: 'gray',
        },
      }}
    >
      {events.map((event, index) => (
        <Box
          key={index}
          sx={{
            position: 'relative',
            mb: 4,
            pl: 40,
            '::before': {
              content: '""',
              position: 'absolute',
              left: 0,
              width: 24,
              height: 24,
              borderRadius: 3,
              bg: 'white',
              border: '2px solid',
              borderColor: 'black',
              boxShadow: 'step',
            },
          }}
        >
          <Box sx={{ fontWeight: 'medium' }}>
            {event.date} {event.time} <span sx={{ fontWeight: 'normal' }}>{event.place}</span>{' '}
            {event.officeCode && (
              <>
                <span>&nbsp;&nbsp;</span>
                <Link to={`/palvelupisteet?id=${event.officeCode}`} sx={{ textDecoration: 'underline' }}>
                  {translate('account.myParcels.showOnMap')}
                </Link>
              </>
            )}
          </Box>
          <Box sx={{ color: 'black' }}>{event.description}</Box>
        </Box>
      ))}
    </Box>
  );
};

export default function TrackingParcel({ parcel, note }) {
  const { t: translate } = useTranslation();
  if (!parcel) return null;

  return (
    <>
      <Box sx={{ mt: 3 }}>
        <Flex>
          <Box sx={{ width: 200, color: 'gray' }}>{translate('tracking.details.parcelNumber')}</Box>
          <Box sx={{ fontWeight: 'medium' }}>{parcel.parcelNumber || '-'}</Box>
        </Flex>
        {!parcel.productCategory || parcel.productCategory.includes('käteinen') ? null : (
          <Flex>
            <Box sx={{ width: 200, color: 'gray' }}>{translate('tracking.details.productCategory')}</Box>
            <Box>{parcel.productCategory}</Box>
          </Flex>
        )}
        <Flex>
          <Box sx={{ width: 200, color: 'gray' }}>{translate('tracking.details.additionalServices')}</Box>
          <Box>{parcel.additionalServices?.length ? reduceServices(parcel.additionalServices) : '-'}</Box>
        </Flex>
        {parcel.storedUntil && (
          <Flex>
            <Box sx={{ width: 200, color: 'gray' }}>{translate('tracking.details.storedUntil')}</Box>
            <Box>{dateToTimezoneDate(parcel.storedUntil)}</Box>
          </Flex>
        )}
        {parcel.deliveryDescription && (
          <Flex sx={{ mt: 2 }}>
            <Box>{parcel.deliveryDescription}</Box>
          </Flex>
        )}
      </Box>
      {parcel.customsStatus && <CustomsClearanceStatus parcel={parcel} />}
      {note && <Box sx={{ mt: 3 }}> <AlertNotification body={note} /></Box>}
      <TrackingEvents events={parcel.trackingEvents} />
    </>
  );
}

function reduceServices(services) {
  const servicesArr = services.map((service) => service.description);
  return servicesArr.join(', ');
}
